import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../services/supabase';
import './OwnerManagement.css';

interface Owner {
  id: string;
  name: string;
  created_at: string;
}

interface OwnerManagementProps {
  onClose: () => void;
  onOwnersChanged: () => void;
}

const OwnerManagement: React.FC<OwnerManagementProps> = ({ onClose, onOwnersChanged }) => {
  const [owners, setOwners] = useState<Owner[]>([]);
  const [newOwnerName, setNewOwnerName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addingOwner, setAddingOwner] = useState(false);

  useEffect(() => {
    loadOwners();
  }, []);

  const loadOwners = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('owners')
        .select('*')
        .order('name', { ascending: true });

      if (fetchError) throw fetchError;
      setOwners(data || []);
    } catch (err) {
      console.error('Error loading owners:', err);
      setError(err instanceof Error ? err.message : 'Failed to load owners');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOwner = async () => {
    const trimmedName = newOwnerName.trim();
    if (!trimmedName) {
      setError('Owner name cannot be empty');
      return;
    }

    try {
      setAddingOwner(true);
      setError(null);
      const { error: insertError } = await supabase
        .from('owners')
        .insert({ name: trimmedName });

      if (insertError) {
        if (insertError.code === '23505') {
          setError('An owner with this name already exists');
        } else {
          throw insertError;
        }
        return;
      }

      setNewOwnerName('');
      await loadOwners();
      onOwnersChanged();
    } catch (err) {
      console.error('Error adding owner:', err);
      setError(err instanceof Error ? err.message : 'Failed to add owner');
    } finally {
      setAddingOwner(false);
    }
  };

  const handleDeleteOwner = async (ownerId: string, ownerName: string) => {
    if (!window.confirm(`Delete owner "${ownerName}"? Cards assigned to this owner will become unassigned.`)) {
      return;
    }

    try {
      setError(null);

      // First, unassign this owner from all cards
      const { error: updateError } = await supabase
        .from('cards')
        .update({ owner: null })
        .eq('owner', ownerName);

      if (updateError) throw updateError;

      // Then delete the owner
      const { error: deleteError } = await supabase
        .from('owners')
        .delete()
        .eq('id', ownerId);

      if (deleteError) throw deleteError;

      await loadOwners();
      onOwnersChanged();
    } catch (err) {
      console.error('Error deleting owner:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete owner');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddOwner();
    } else if (e.key === 'Escape') {
      setNewOwnerName('');
      setError(null);
    }
  };

  const modalContent = (
    <div className="owner-management-overlay" onClick={onClose}>
      <div className="owner-management-modal" onClick={(e) => e.stopPropagation()}>
        <div className="owner-management-header">
          <h2>Manage Owners</h2>
          <button className="owner-management-close" onClick={onClose}>×</button>
        </div>

        <div className="owner-management-content">
          {error && (
            <div className="owner-management-error">{error}</div>
          )}

          <div className="owner-management-add">
            <input
              type="text"
              className="owner-management-input"
              placeholder="Enter owner name..."
              value={newOwnerName}
              onChange={(e) => setNewOwnerName(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={addingOwner}
            />
            <button
              className="owner-management-add-btn"
              onClick={handleAddOwner}
              disabled={addingOwner || !newOwnerName.trim()}
            >
              {addingOwner ? 'Adding...' : '+ Add Owner'}
            </button>
          </div>

          {loading ? (
            <div className="owner-management-loading">Loading owners...</div>
          ) : (
            <div className="owner-management-list">
              {owners.length === 0 ? (
                <div className="owner-management-empty">No owners yet. Add your first owner above.</div>
              ) : (
                owners.map((owner) => (
                  <div key={owner.id} className="owner-management-item">
                    <div className="owner-management-item-info">
                      <span className="owner-management-icon">👤</span>
                      <span className="owner-management-name">{owner.name}</span>
                    </div>
                    <button
                      className="owner-management-delete-btn"
                      onClick={() => handleDeleteOwner(owner.id, owner.name)}
                      title={`Delete ${owner.name}`}
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default OwnerManagement;
